<template>
  <div class="header__wrapper" :class="{ 'header--sticky': sticky }">
    <al-header
      :description="$t('pageTitles.default')"
      :networks="basic ? null : ['mastercard']"
      :id-check="idCheck"
      :issuer="issuer"
      :light="light"
      :with-back-button="showBackButton"
      :back-button-label="$t('button.goBack')"
      @click-back-button="handleBackButton">
      <a
        v-if="showCloseButton"
        key="cancel-challenge-key"
        v-analytics:click="$EVENTS.CLOSE_BUTTON"
        :aria-label="$t('accessibility.close')"
        role="button"
        class="header__item"
        data-test="cancel_challenge"
        href="#"
        @click="handleCloseButton">
        <i class="icon-close" />
      </a>
      <a
        v-if="showMenu"
        key="hamburger-menu-key"
        v-toggle:al-menu="'menu'"
        :aria-label="$t('accessibility.openMenu')"
        href="#"
        role="button"
        class="header__item"
        data-test="hamburger-menu">
        <i class="icon-menu" />
      </a>
      <a
        v-else-if="showLocaleButton"
        key="locale-selector-key"
        v-toggle:al-action-sheet="'locale-action-sheet'"
        href="#"
        role="button"
        class="header__item"
        data-test="locale-selection-button">
        <span>{{ currentCountry }}</span>
        <i class="icon-globe" />
      </a>
    </al-header>
    <al-menu :close-button-label="$t('accessibility.closeMenu')" name="menu">
      <template slot-scope="slotProps">
        <li v-for="page in menuOptions" :key="page.name">
          <a
            :data-test="page.ref"
            href="#"
            @click.prevent="handlePageClick(page.route, slotProps)"
            @keyup.enter="handlePageClick(page.route, slotProps)">
            {{ $t(page.name) }}
          </a>
        </li>
      </template>
    </al-menu>
  </div>
</template>

<script>
import { AlHeader, AlMenu } from '@mc-alberta/alcapone'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import useConfirmModal from '@/composables/useConfirmModal'
import { ROUTES } from '@/constants'
import * as types from '@/store/mutation-types.js'

export default {
  name: 'PageHeader',
  components: { AlHeader, AlMenu },
  props: {
    light: {
      type: Boolean,
      default: false
    },
    backButtonRoute: {
      type: Object,
      default: null
    },
    basic: {
      type: Boolean,
      default: false
    },
    hideMenu: {
      type: Boolean,
      default: false
    },
    hideLocaleButton: {
      type: Boolean,
      default: false
    },
    idCheck: {
      type: Boolean,
      default: false
    },
    canCancelChallenge: {
      type: Boolean,
      default: false
    },
    showCloseButton: {
      type: Boolean,
      default: false
    },
    showIssuer: {
      type: Boolean,
      default: false
    },
    sticky: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const { setConfirmModal } = useConfirmModal({ context })
    return { setConfirmModal }
  },
  data() {
    return {
      pages: [
        {
          name: 'menu.home',
          ref: 'home-page-link',
          route: ROUTES.PROFILE_HOME
        },
        {
          name: 'menu.accounts',
          ref: 'cards-page-link',
          route: ROUTES.LIST_CARDS
        },
        {
          name: 'menu.shipping',
          ref: 'shipping-page-link',
          route: ROUTES.LIST_SHIPPING
        },
        {
          name: 'menu.setting',
          ref: 'settings-page-link',
          route: ROUTES.SETTINGS
        },
        {
          name: 'menu.support',
          ref: 'support-page-link',
          route: ROUTES.SUPPORT
        },
        {
          name: 'menu.exit',
          ref: 'login-page-link',
          route: ROUTES.ENTRY
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['isLoading', 'isLoggedIn', 'issuerName', 'locale']),
    ...mapState({
      currentCountry: (state) => state.user.consumer.countryCode,
      mustAcceptPolicy: (state) => state.mustAcceptPolicy,
      mustReaccept: (state) => state.mustReaccept
    }),
    menuOptions() {
      const excludedMenuOptions = []

      if (this.$i18nData.isCountryRestricted(this.locale)) {
        excludedMenuOptions.push(ROUTES.LIST_SHIPPING)
      }

      return this.pages.filter(({ route }) => !excludedMenuOptions.includes(route))
    },
    issuer() {
      return this.showIssuer && this.issuerName ? { name: this.issuerName } : null
    },
    showBackButton() {
      return this.backButtonRoute && Object.keys(this.backButtonRoute).length > 0
    },
    showMenu() {
      return (
        !this.hideMenu &&
        !this.showLocaleButton &&
        this.isLoggedIn &&
        !this.isLoading &&
        !this.mustAcceptPolicy &&
        !this.mustReaccept
      )
    },
    showLocaleButton() {
      return !this.isLoggedIn && !this.hideLocaleButton && !this.isLoading
    }
  },
  methods: {
    ...mapActions(['logout']),
    ...mapMutations({
      setSkipUnprovision: types.SET_SKIP_UNPROVISION
    }),
    handleBackButton() {
      this.$router.replace(this.backButtonRoute)
    },
    handleCloseButton() {
      if (this.isLoggedIn && !this.canCancelChallenge) {
        this.setConfirmModal({ visible: true, routeTo: { name: ROUTES.LIST_CARDS } })
      } else {
        this.$root.$emit('alcapone-toggle:al-modal', 'cancel-challenge-modal')
      }
    },
    handlePageClick(name, slotProps) {
      // istanbul ignore else
      if (typeof slotProps?.closeMenu === 'function') {
        slotProps.closeMenu()
      }

      const route = { name }
      if (name === ROUTES.ENTRY) {
        route.params = { logout: true }
      } else if (this.$route.name === ROUTES.LIST_CARDS_UNPROVISIONED) {
        this.setSkipUnprovision(true)
      }

      this.$router.push(route)
    }
  }
}
</script>
